import axios from 'axios';

const TOKEN_KEY = 'SmartDental_token';
const READONLY_KEY = 'SmartDental_readonly';

const MUTANT_METHODS = ['POST', 'PUT', 'DELETE', 'PATCH'];
const READONLY_WHITELIST = ['/auth/logout', '/auth/refresh', '/auth/demo-login'];

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

async function refreshAccessToken(): Promise<string> {
  const { data } = await api.post('/auth/refresh');
  return data.access_token;
}

// Request interceptor: adjunta Authorization Bearer + bloquea mutaciones en modo demo
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }

  // Bloquear peticiones mutantes en modo demo read-only
  const method = config.method?.toUpperCase() ?? '';
  if (MUTANT_METHODS.includes(method)) {
    const isReadOnly = sessionStorage.getItem(READONLY_KEY) === 'true';
    const isWhitelisted = READONLY_WHITELIST.some((path) => config.url?.includes(path));

    if (isReadOnly && !isWhitelisted) {
      // Emitir evento para que un listener global muestre el toast
      window.dispatchEvent(
        new CustomEvent('readonly-blocked', {
          detail: { url: config.url, method: config.method },
        })
      );
      return Promise.reject(
        new axios.Cancel('Operación bloqueada: modo demo de solo lectura')
      );
    }
  }

  return config;
});

// Response interceptor: refresh automatico en 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (originalRequest.url === '/auth/refresh') {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber((token) => {
            originalRequest.headers.set('Authorization', `Bearer ${token}`);
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        if (newToken) {
          sessionStorage.setItem(TOKEN_KEY, newToken);
        }
        isRefreshing = false;
        onTokenRefreshed(newToken);
        originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        refreshSubscribers = [];
        sessionStorage.removeItem(TOKEN_KEY);
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status === 403) {
      const message = error.response?.data?.message;
      if (message?.includes('bloqueada')) {
        sessionStorage.removeItem(TOKEN_KEY);
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
